/**
 *
 * "Display a table to show the History of the incoming Deck."
 *
 * @file   HistoryTable.js
 * @author Lateral
 * @since  2023
 */

import { Box, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import dayjs from 'dayjs'
import { ContextMenu, SCOPES } from 'components'
import React, { useState } from 'react'
import BigNumber from 'bignumber.js'
import { dateFormat } from 'common/dates'
import { parsedRevisionNumberWithSubLetter } from 'common/revisionNumber'
import { useCurrentUser } from 'hooks'
import { DeckAccordion } from 'pages/deck/common/DeckAccordion'

function HistoryTable({
  deckRevisionId,
  historyId,
  deckRevisions,
  deckRevisionHistories,
  onClick,
  onEditDetails,
  onEditHistory,
  onEditMaintenance,
  onViewUpdates,
  onExpanded
}) {
  /**
   *  Create a table of Deck history from record of maintenance and revisions
   *
   * @function
   * @param {number} deckRevisionId - Deck revision ID
   * @param {number} historyId - Deck mentenance ID
   * @param {object[]} deckRevisions - array of deck revision/trial objects
   * @param {object[]} deckRevisionHistories - array of deck maintenance objects
   * @param {} onclick - define the action on clicking a row
   * @param {} onEditDetails - define the action on editing details of maintenance such as date and/or description
   * @param {} onRestore - define the action on restore revision
   * @param {} onEditHistory - define the action on edit details of revision
   * @param {} onEditMaintenance - define the action on edit details of maintenance
   *
   * @returns {object} - Dashboard page
   */
  const theme = useTheme()

  const { currentUser } = useCurrentUser()
  const data = getData()
  const maxRevision = Math.max(...deckRevisions.map((d) => d.RevisionNumber))
  const editableRevision = new BigNumber(maxRevision).minus(1).toNumber()
  const unpublishedSites = currentUser.getSitesForScope(SCOPES.deckLayoutReview.Write)
  const maintenanceSites = currentUser.getSitesForScope(SCOPES.deckMaintenance.Write)
  const [showFullList, setShowFullList] = useState(false)

  //history data is divided between the DeckRevision and maintenance tables.
  function getData() {
    /**
     *  Create an array of deck history including Maintenances and Revisions sorted based on revision number
     * @function
     * @returns {Array} - Maintenance and revision histories of incoming deck
     */

    // First map the revisions in history
    let histories = deckRevisions.map((revision) => {
      return {
        id: revision.id,
        historyId: '',
        siteId: revision.SiteId,
        revisionNumber: revision.RevisionNumber,
        minorRevisionNumber: revision.MinorRevisionNumber,
        revisionNumberToView: parsedRevisionNumberWithSubLetter(revision),
        type: revision.Type
          ? revision.Type
          : revision.IsTrial
          ? 'Trial'
          : revision.IsProposal
          ? 'Proposal'
          : revision.IsPublished
          ? 'Revision'
          : 'Proposal',
        description: revision.Description,
        reported: dayjs(revision.createdAt),
        created: dayjs(revision.createdAt),
        user: revision.CreatedBy,
        isArchived: revision.IsArchived,
        isProposal: revision.IsProposal,
        isTrial: revision.IsTrial,
        isOld: true
      }
    })

    // If the event is a maintenance add it to the same array
    if (deckRevisionHistories) {
      histories = histories.concat(
        deckRevisionHistories.map((history) => {
          const revision = deckRevisions?.find((d) => d.id === history.RevisionId)
          return {
            id: revision?.id,
            historyId: history.id,
            siteId: history.SiteId,
            isHistory: true,
            revisionNumber: revision?.RevisionNumber,
            minorRevisionNumber: revision?.MinorRevisionNumber,
            revisionNumberToView: revision ? parsedRevisionNumberWithSubLetter(revision) : 0,
            type: 'Maintenance',
            description: history.ShortDescription,
            reported: dayjs(history.DatePerformed ?? history.createdAt),
            created: dayjs(history.createdAt),
            user: history.CreatedBy,
            updates: history.Updates,
            isArchived: revision.IsArchived,
            isProposal: revision.IsProposal,
            isTrial: revision.IsTrial,
            isOld: true
          }
        })
      )
    }

    histories = histories.sort((a, b) => {
      return b.created - a.created
    })
    histories[0].isOld = false
    return histories
  }

  function onRowClick(id, historyId) {
    /**
     *  call the input function to the HistoryTable on clicking a row.
     */
    onClick(id, historyId)
  }

  const TableComponent = (
    <TableContainer
      sx={{
        maxHeight: showFullList ? '100%' : '300px',
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          width: '2px',
          height: '2px'
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: theme.palette.supporting.dark
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: theme.palette.supporting.pale,
          borderRadius: 2
        }
      }}>
      <Table stickyHeader sx={{ borderCollapse: 'collapse' }}>
        <TableHead>
          <TableRow>
            <TableCell>Revision</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Works Date</TableCell>
            <TableCell>Reported</TableCell>
            <TableCell>User</TableCell>
            <TableCell sx={{ width: '0' }}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((history) => {
            const isHighlighted = deckRevisionId === history.id && historyId === history.historyId
            let menuItems = [{ title: 'Edit Details', action: () => onEditDetails(history) }]

            if (history.isHistory) {
              menuItems.push({
                title: 'Edit Maintenance',
                condition: history.isHistory,
                action: () => onEditMaintenance(history)
              })

              if (history.updates?.length) {
                menuItems.push({
                  title: 'View Updates',
                  condition: history.isHistory,
                  action: () => onViewUpdates(history)
                })
              }
            } else {
              if (editableRevision <= history.revisionNumber && !history.isTrial) {
                menuItems.push({
                  title: 'Edit Revision',
                  condition: history.isHistory,
                  action: () => onEditHistory(history)
                })
              }
            }
            return (
              <TableRow
                key={history.historyId ? history.historyId : history.id}
                hover
                sx={{ backgroundColor: isHighlighted ? theme.palette.primary.light : '' }}
                onClick={() => onRowClick(history)}>
                <TableCell>{history.revisionNumberToView}</TableCell>
                <TableCell>{history.type}</TableCell>
                <TableCell>{history.description}</TableCell>
                <TableCell>{history.reported.format(dateFormat)}</TableCell>
                <TableCell>{history.created.format(dateFormat)}</TableCell>
                <TableCell sx={{ width: 'auto' }}>{history.user}</TableCell>
                <TableCell sx={{ width: '0' }}>
                  {currentUser.isAdmin ||
                  (history.isHistory && maintenanceSites.includes(history.siteId)) ||
                  (!history.isHistory && unpublishedSites.includes(history.siteId)) ? (
                    <ContextMenu context={history} menuItems={menuItems} />
                  ) : (
                    false
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )

  // Return a table of deck revision and maintenance history sorted by revision number
  return (
    <>
      <DeckAccordion
        defaultExpanded={false}
        showTooltip={false}
        title="Deck history"
        onChange={(val) => {
          setShowFullList(val)
          onExpanded(val)
        }}>
        {TableComponent}
      </DeckAccordion>
      {!showFullList && (
        <Box
          sx={{
            marginBottom: '1em',
            height: '310px'
          }}>
          {TableComponent}

          <Divider />
        </Box>
      )}
    </>
  )
}

export default HistoryTable
